import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { AuthProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { I18nProvider } from '@wix/cli-i18n';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { DebugLogProvider } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import type { Flow } from '@wix/cli-telemetry';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import type { CommandServices } from '@wix/cli-command-contract';
import type { Command } from '@commander-js/extra-typings';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import type { ProjectModel } from '../model.js';
import type { DevCenterComponent } from '../dev-center/components/dev-center-component.js';
import { loadProjectModel, ProjectModelProvider } from '../model.js';
import messages from '../messages.json' with { type: 'json' };
import packageJson from '../../package.json' with { type: 'json' };

interface Options {
  watchForModelChanges?: boolean;
  skipProjectValidation?: boolean;
}

async function initServices({
  model,
  services,
  command,
}: {
  model: ProjectModel;
  services: CommandServices;
  command: Command;
}) {
  const { biLogger, errorReporter, panorama } = services;

  const project = 'Apps' satisfies Flow;

  errorReporter.setTag('project', project);
  errorReporter.setTag('project.app_id', model.config.appId);
  errorReporter.setTag('project.id', model.config.projectId);

  panorama.setDefaultClientData({
    cliProject: project,
    cliProjectId: model.config.projectId,
    cliAppId: model.config.appId,
  });

  biLogger.updateDefaults({
    flow: project,
    command: command.name(),
    cliVersion: packageJson.version,
    _appId: model.config.appId,
  });
}

function getExtraBiFields(command: string, components: DevCenterComponent[]) {
  switch (command) {
    case 'dev':
    case 'preview':
      return {
        is_mocking_values: components.some(
          (component) =>
            component.type === 'EmbeddedScript' && component.paramsOverride
        ),
      };
    default:
      return;
  }
}

export async function renderCommand(
  command: Command,
  services: CommandServices,
  tree: ReactElement,
  options?: Options
) {
  await runCommand(command, services, async (model) => {
    const { biLogger, errorReporter, panorama } = services;

    if (model.components.length === 0 && !options?.skipProjectValidation) {
      throw new CliError({
        code: CliErrorCode.NoExtensionsFound(),
        cause: null,
      });
    }

    const debugLogFilePath = getDebugLogFilePath(model.projectFolder);

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    const headers = buildHttpClientHeaders({
      flow: 'Apps',
      command: command.name(),
      cliVersion: packageJson.version,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <AuthProvider onLogin={handleLogin}>
              <HttpClientProvider headers={headers}>
                <I18nProvider messages={messages}>
                  <DebugLogProvider filePath={debugLogFilePath}>
                    <ProjectModelProvider
                      value={model}
                      watchForChanges={options?.watchForModelChanges}
                    >
                      {tree}
                    </ProjectModelProvider>
                  </DebugLogProvider>
                </I18nProvider>
              </HttpClientProvider>
            </AuthProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

export async function runCommand(
  command: Command,
  services: CommandServices,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd());
  await initServices({ model, command, services });

  const commandStatus = reportCommandStartEvent({
    biLogger: services.biLogger,
    command,
    cliVersion: packageJson.version,
    flow: 'Apps',
    projectId: model.config.projectId,
    extensions: model.components.reduce<Record<string, number>>((acc, item) => {
      const value = acc[item.type];
      acc[item.type] = value ? value + 1 : 1;
      return acc;
    }, {}),
    extraFields: getExtraBiFields(command.name(), model.components),
  });

  const transaction = createCommandTransaction(services.panorama, command);

  try {
    transaction.start();
    await commandFn(model);
    transaction.finish();
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
